import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, Package, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const CATEGORIES = {
  power_tools: { label: 'Power Tools', icon: '⚡' },
  hand_tools: { label: 'Hand Tools', icon: '🔧' },
  safety_equipment: { label: 'Safety Equipment', icon: '🦺' },
  measuring_tools: { label: 'Measuring Tools', icon: '📏' },
  other: { label: 'Other', icon: '📦' }
};

const ProjectToolsSection = ({ projectId, projectName, user }) => {
  const [projectTools, setProjectTools] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [notes, setNotes] = useState('');
  const [checkinCondition, setCheckinCondition] = useState('good');

  // Only show for employees and admins
  const canManageTools = user.role === 'employee' || user.role === 'admin' || user.role === 'supervisor';

  useEffect(() => {
    if (canManageTools) {
      fetchTools();
    }
  }, [projectId, canManageTools]);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const [projectRes, availableRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/tools`),
        axios.get(`${API}/tools?status=available`)
      ]);
      setProjectTools(projectRes.data);
      setAvailableTools(availableRes.data);
    } catch (error) {
      console.error('Failed to fetch tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedTool) return;
    try {
      await axios.post(`${API}/tools/${selectedTool}/checkout`, {
        project_id: projectId,
        notes: notes
      });
      toast.success('Tool checked out successfully!');
      setCheckoutDialogOpen(false);
      setSelectedTool(null);
      setNotes('');
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out tool');
    }
  };

  const handleCheckin = async () => {
    if (!selectedTool) return;
    try {
      await axios.post(`${API}/tools/${selectedTool}/checkin`, {
        notes: notes,
        condition: checkinCondition
      });
      toast.success('Tool checked in successfully!');
      setCheckinDialogOpen(false);
      setSelectedTool(null);
      setNotes('');
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in tool');
    }
  };

  const openCheckinDialog = (tool) => {
    setSelectedTool(tool.id);
    setCheckinCondition(tool.condition || 'good');
    setNotes('');
    setCheckinDialogOpen(true);
  };

  if (!canManageTools) {
    return null;
  }

  const myToolsHere = projectTools.filter(t => t.current_holder_id === user.id);
  const otherToolsHere = projectTools.filter(t => t.current_holder_id !== user.id);

  return (
    <div className="glass-card p-4 sm:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Wrench className="w-6 h-6 text-orange-400" />
          Project Tools
        </h2>
        {user.role === 'employee' && (
          <Button 
            onClick={() => setCheckoutDialogOpen(true)} 
            className="bg-orange-600 hover:bg-orange-700"
            size="sm"
          >
            <Package className="w-4 h-4 mr-2" />
            Check Out Tool
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : (
        <>
          {/* My Tools at this project */}
          {myToolsHere.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-400 mb-2 uppercase">My Tools Here</h3>
              <div className="space-y-2">
                {myToolsHere.map(tool => {
                  const category = CATEGORIES[tool.category] || CATEGORIES.other;
                  return (
                    <div key={tool.id} className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{category.icon}</span>
                        <div>
                          <p className="text-white font-medium">{tool.name}</p>
                          <p className="text-slate-400 text-xs">{category.label}</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => openCheckinDialog(tool)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Check In
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Other tools at this project */}
          {otherToolsHere.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-400 mb-2 uppercase">Other Tools Here</h3>
              <div className="space-y-2">
                {otherToolsHere.map(tool => {
                  const category = CATEGORIES[tool.category] || CATEGORIES.other;
                  return (
                    <div key={tool.id} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{category.icon}</span>
                        <div>
                          <p className="text-white font-medium">{tool.name}</p>
                          <p className="text-slate-400 text-xs">
                            Held by: {tool.current_holder_name}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-blue-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {tool.last_checked_out ? new Date(tool.last_checked_out).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }) : 'N/A'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {projectTools.length === 0 && (
            <div className="text-center py-6 text-slate-400">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No tools currently at this project</p>
            </div>
          )}
        </>
      )}

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
        <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-xl">Check Out Tool to {projectName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-700">Select Tool *</Label>
              <Select value={selectedTool || ''} onValueChange={setSelectedTool}>
                <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                  <SelectValue placeholder="Choose a tool..." />
                </SelectTrigger>
                <SelectContent>
                  {availableTools.length === 0 ? (
                    <SelectItem value="none" disabled>No tools available</SelectItem>
                  ) : (
                    availableTools.map(tool => (
                      <SelectItem key={tool.id} value={tool.id}>
                        {CATEGORIES[tool.category]?.icon} {tool.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-gray-700">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes..."
                className="text-gray-900 bg-white border-gray-300"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setCheckoutDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCheckout} 
                disabled={!selectedTool}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Check Out
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkin Dialog */}
      <Dialog open={checkinDialogOpen} onOpenChange={setCheckinDialogOpen}>
        <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-xl">Check In Tool</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-700">Tool Condition</Label>
              <Select value={checkinCondition} onValueChange={setCheckinCondition}>
                <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-gray-700">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes about the tool..."
                className="text-gray-900 bg-white border-gray-300"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setCheckinDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCheckin} className="bg-green-600 hover:bg-green-700">
                Check In
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectToolsSection;
